import { parseAsUTC } from 'src/app/helpers/parse-as-utc';
import { CustomRoute } from '../interfaces/custom-route.interface';
import { START_TIME_HOUR } from '../shared/planner/components/route-timeline/config/constants';

/**
 * Resolves the hour (0-23) where the planner timeline grid starts (HRS-2640).
 *
 * The grid start is dynamic per route: a route starting at 07:00 shows the
 * 07:00-24:00 grid every day; a route starting at 11:00 shows no blank slots
 * before 11:00.
 *
 * Sources, combined with `min()`:
 * - `declaredStartTime`: the "HH:MM" chosen at registration
 *   (`registerUserBeforeFormValues.startTime`) or the template's
 *   `recommended_route.start_time`.
 * - Derived minimum: earliest first-point hour across days. Guards against
 *   routes whose days start before the declared hour (e.g. routes planned
 *   before the backend applied the chosen hour to every day), so no point can
 *   ever fall above the top of the grid.
 *
 * Falls back to `START_TIME_HOUR` (10) when neither source is available, which
 * keeps legacy routes pixel-identical to the previous fixed grid.
 */
export function getRouteStartHour(
  route: Pick<CustomRoute, 'dailyRoutes'> | null | undefined,
  declaredStartTime?: string | null,
): number {
  const declaredHour = parseDeclaredHour(declaredStartTime);
  const derivedHour = deriveMinFirstPointHour(route);

  const candidates = [declaredHour, derivedHour].filter(
    (hour): hour is number => hour !== null,
  );
  if (!candidates.length) return START_TIME_HOUR;

  return Math.min(Math.max(Math.min(...candidates), 0), 23);
}

/** Parses "HH:MM" into an hour (0-23); null when missing or malformed. */
function parseDeclaredHour(value?: string | null): number | null {
  if (!value || !/^([01]?\d|2[0-3]):[0-5]\d$/.test(value)) return null;
  return Number(value.split(':')[0]);
}

/** Earliest first-point start hour across days with points; null if none. */
function deriveMinFirstPointHour(
  route: Pick<CustomRoute, 'dailyRoutes'> | null | undefined,
): number | null {
  const firstPointHours = (route?.dailyRoutes ?? [])
    .filter((day) => day.points?.length)
    .map((day) => parseAsUTC(day.points[0].startDate).getUTCHours());

  return firstPointHours.length ? Math.min(...firstPointHours) : null;
}
